import React, { useEffect, useState, useMemo } from "react";
import { toast } from "react-hot-toast";
import Select from "react-select";
import { Plus, Trash2, ChevronDown, ChevronRight, HelpCircle, Edit, Globe } from "lucide-react";
import { languages } from "../constants/Languages";
import apiService from "../services/api";
import { extractTourText } from "../utils/tourUtils";
import Button from "../components/UI/Button";
import Modal from "../components/UI/Modal";

// ─── Constants ──────────────────────────────────────────────────────────────

const SUPPORT_CATEGORIES = [
  { key: "getting_started", label: "Getting Started" },
  { key: "journey_planning", label: "Journey Planning" },
  { key: "feature_functionality", label: "Feature & Functionality" },
  { key: "buying_details", label: "Buying & Downloading Details" },
  { key: "navigation_routes", label: "Tour Navigation & Routes" },
] as const;

type SupportCategoryKey = typeof SUPPORT_CATEGORIES[number]["key"];

interface LanguageTextPair {
  language: string;
  text: string;
}

interface FaqItem {
  id: number;
  type: string;
  category: string;
  tourId: number | null;
  tour?: { id: number; name: any };
  name: LanguageTextPair[] | string | null;
  topQuestion: LanguageTextPair[] | string | null;
  description: LanguageTextPair[] | string | null;
  status: boolean;
}

// ─── Helpers ────────────────────────────────────────────────────────────────

const extractText = (data: any, lang = "en"): string => {
  if (!data) return "";
  if (typeof data === "string") {
    try {
      const parsed = JSON.parse(data);
      if (Array.isArray(parsed)) {
        const match = parsed.find((p: any) => p.language === lang);
        return match?.text || parsed[0]?.text || "";
      }
      return data;
    } catch {
      return data;
    }
  }
  if (Array.isArray(data)) {
    const match = data.find((p: any) => p.language === lang);
    return match?.text || data[0]?.text || "";
  }
  return "";
};

const parseToArray = (value: any): LanguageTextPair[] => {
  if (!value) return [];
  if (Array.isArray(value)) return value.filter((v: any) => v?.language || v?.text);
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      if (Array.isArray(parsed)) return parsed;
    } catch {}
    return [{ language: "en", text: value }];
  }
  return [];
};

// ─── Main Component ─────────────────────────────────────────────────────────

const FaqComponent = () => {
  const [allFaq, setAllFaq] = useState<FaqItem[]>([]);
  const [tours, setTours] = useState<any[]>([]);
  const [activeTab, setActiveTab] = useState<"support" | "tour">("support");
  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(
    new Set(SUPPORT_CATEGORIES.map((c) => c.key))
  );

  // Modal state
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingFaq, setEditingFaq] = useState<FaqItem | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Form fields
  const [modalType, setModalType] = useState<"support" | "tour">("support");
  const [modalCategory, setModalCategory] = useState<string>("getting_started");
  const [selectedTourIds, setSelectedTourIds] = useState<string[]>([]);
  const [questionTranslations, setQuestionTranslations] = useState<LanguageTextPair[]>([
    { language: "en", text: "" },
  ]);
  const [answerTranslations, setAnswerTranslations] = useState<LanguageTextPair[]>([
    { language: "en", text: "" },
  ]);

  // ─── Data fetching ──────────────────────────────────────────────────────

  const fetchFaqs = async () => {
    try {
      const res = await apiService.getAllFaq();
      const resData = res?.data?.data;
      setAllFaq(Array.isArray(resData) ? resData : []);
    } catch {
      setAllFaq([]);
    }
  };

  const fetchTours = async () => {
    try {
      const res = await apiService.getTours("", "", 1, 1000);
      setTours(res.data || []);
    } catch {
      setTours([]);
    }
  };

  useEffect(() => {
    fetchFaqs();
    fetchTours();
  }, []);

  // ─── Derived data ───────────────────────────────────────────────────────

  const supportFaqs = useMemo(
    () => allFaq.filter((f) => f.type === "support"),
    [allFaq]
  );

  const tourFaqs = useMemo(
    () => allFaq.filter((f) => f.type === "tour"),
    [allFaq]
  );

  const tourFaqsByTour = useMemo(() => {
    const map: Record<number, { tourName: string; faqs: FaqItem[] }> = {};
    tourFaqs.forEach((faq) => {
      const tid = faq.tourId || 0;
      if (!map[tid]) {
        const tourName = faq.tour
          ? extractTourText(faq.tour.name)
          : `Tour #${tid}`;
        map[tid] = { tourName, faqs: [] };
      }
      map[tid].faqs.push(faq);
    });
    return map;
  }, [tourFaqs]);

  const tourOptions = useMemo(() => {
    return tours.map((tour: any) => ({
      value: String(tour.id),
      label: extractTourText(tour.name),
    }));
  }, [tours]);

  // ─── Modal helpers ──────────────────────────────────────────────────────

  const resetForm = () => {
    setEditingFaq(null);
    setModalCategory("getting_started");
    setSelectedTourIds([]);
    setQuestionTranslations([{ language: "en", text: "" }]);
    setAnswerTranslations([{ language: "en", text: "" }]);
  };

  const openCreateModal = (type: "support" | "tour", category?: string) => {
    resetForm();
    setModalType(type);
    if (type === "support" && category) {
      setModalCategory(category);
    }
    if (type === "tour") {
      setModalCategory("general");
    }
    setIsModalOpen(true);
  };

  const openEditModal = (faq: FaqItem) => {
    setEditingFaq(faq);
    setModalType(faq.type as "support" | "tour");
    setModalCategory(faq.category);
    setSelectedTourIds(faq.tourId ? [String(faq.tourId)] : []);

    const q = parseToArray(faq.topQuestion);
    const a = parseToArray(faq.description);
    setQuestionTranslations(q.length > 0 ? q : [{ language: "en", text: "" }]);
    setAnswerTranslations(a.length > 0 ? a : [{ language: "en", text: "" }]);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    resetForm();
  };

  // ─── Translation row management ────────────────────────────────────────

  const addTranslationRow = () => {
    const lastQ = questionTranslations[questionTranslations.length - 1];
    if (lastQ && !lastQ.language && !lastQ.text) {
      toast.error("Please fill in the current language before adding another");
      return;
    }
    setQuestionTranslations([...questionTranslations, { language: "", text: "" }]);
    setAnswerTranslations([...answerTranslations, { language: "", text: "" }]);
  };

  const removeTranslationRow = (index: number) => {
    if (questionTranslations.length <= 1) {
      toast.error("At least one language is required");
      return;
    }
    setQuestionTranslations(questionTranslations.filter((_, i) => i !== index));
    setAnswerTranslations(answerTranslations.filter((_, i) => i !== index));
  };

  const updateLanguageForRow = (index: number, lang: string) => {
    setQuestionTranslations(
      questionTranslations.map((item, i) => (i === index ? { ...item, language: lang } : item))
    );
    setAnswerTranslations(
      answerTranslations.map((item, i) => (i === index ? { ...item, language: lang } : item))
    );
  };

  // ─── Submit ─────────────────────────────────────────────────────────────

  const handleSubmit = async () => {
    const filteredQ = questionTranslations.filter((t) => t.text.trim() && t.language);
    const filteredA = answerTranslations.filter((t) => t.text.trim() && t.language);

    if (filteredQ.length === 0 || filteredA.length === 0) {
      toast.error("Please fill in at least one language for question and answer");
      return;
    }

    if (modalType === "tour" && selectedTourIds.length === 0 && !editingFaq) {
      toast.error("Please select at least one tour");
      return;
    }

    setIsSubmitting(true);
    try {
      if (editingFaq) {
        // Update single FAQ
        const payload = {
          type: modalType,
          category: modalCategory,
          tourId: modalType === "tour" && selectedTourIds.length > 0 ? Number(selectedTourIds[0]) : null,
          topQuestion: filteredQ,
          description: filteredA,
          name: filteredQ, // keep name in sync
          status: true,
        };
        const res = await apiService.editFaq(editingFaq.id, payload);
        if (res.status === 200) {
          toast.success("FAQ updated successfully");
          fetchFaqs();
          handleCloseModal();
        } else {
          toast.error("Failed to update FAQ");
        }
      } else {
        // Create — for tour type, create one FAQ per selected tour
        const tourIdsToCreate =
          modalType === "tour" ? selectedTourIds : [null];

        for (const tourId of tourIdsToCreate) {
          const payload = {
            type: modalType,
            category: modalCategory,
            tourId: tourId ? Number(tourId) : null,
            topQuestion: filteredQ,
            description: filteredA,
            name: filteredQ,
            status: true,
          };
          const res = await apiService.postAddFaq(payload);
          if (res.status !== 201) {
            toast.error("Failed to create FAQ");
            setIsSubmitting(false);
            return;
          }
        }
        toast.success("FAQ created successfully");
        fetchFaqs();
        handleCloseModal();
      }
    } catch {
      toast.error("Something went wrong");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ─── Delete ─────────────────────────────────────────────────────────────

  const handleDelete = async (id: number) => {
    try {
      const res = await apiService.removeAddFaq(id);
      if (res.status === 200) {
        toast.success("FAQ deleted successfully");
        fetchFaqs();
      } else {
        toast.error("Failed to delete FAQ");
      }
    } catch {
      toast.error("Something went wrong");
    }
  };

  // ─── Toggle category accordion ──────────────────────────────────────────

  const toggleCategory = (key: string) => {
    setExpandedCategories((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  // ─── Render helpers ─────────────────────────────────────────────────────

  const renderFaqRow = (faq: FaqItem) => {
    const question = extractText(faq.topQuestion);
    const answer = extractText(faq.description);
    const langCount = parseToArray(faq.topQuestion).length;

    return (
      <div
        key={faq.id}
        className="flex items-start justify-between py-3 px-4 bg-white border border-gray-200 rounded-lg mb-2 hover:shadow-sm transition-shadow"
      >
        <div className="flex-1 min-w-0 mr-4">
          <p className="font-medium text-gray-900 text-sm truncate">
            {question || "No question"}
          </p>
          <p className="text-gray-500 text-xs mt-1 line-clamp-2">
            {answer || "No answer"}
          </p>
          {langCount > 1 && (
            <span className="inline-flex items-center gap-1 text-xs text-blue-600 mt-1">
              <Globe className="h-3 w-3" />
              {langCount} languages
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <button
            onClick={() => openEditModal(faq)}
            className="p-1.5 text-gray-400 hover:text-blue-600 rounded hover:bg-blue-50 transition-colors"
            title="Edit"
          >
            <Edit className="h-4 w-4" />
          </button>
          <button
            onClick={() => {
              if (window.confirm("Are you sure you want to delete this FAQ?")) {
                handleDelete(faq.id);
              }
            }}
            className="p-1.5 text-gray-400 hover:text-red-600 rounded hover:bg-red-50 transition-colors"
            title="Delete"
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      </div>
    );
  };

  // ─── Render ─────────────────────────────────────────────────────────────

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">FAQs</h1>
        <p className="text-gray-500 text-sm">Manage support and tour-specific frequently asked questions</p>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-gray-200 mb-6">
        <button
          onClick={() => setActiveTab("support")}
          className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "support"
              ? "border-blue-600 text-blue-600"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          Global FAQs (Support Page)
        </button>
        <button
          onClick={() => setActiveTab("tour")}
          className={`px-5 py-3 text-sm font-medium border-b-2 transition-colors ${
            activeTab === "tour"
              ? "border-blue-600 text-blue-600"
              : "border-transparent text-gray-500 hover:text-gray-700"
          }`}
        >
          Tour FAQs (General)
        </button>
      </div>

      {/* ─── Section 1: Support FAQs ─── */}
      {activeTab === "support" && (
        <div className="space-y-4">
          {SUPPORT_CATEGORIES.map((cat) => {
            const catFaqs = supportFaqs.filter((f) => f.category === cat.key);
            const isExpanded = expandedCategories.has(cat.key);

            return (
              <div key={cat.key} className="border border-gray-200 rounded-lg overflow-hidden">
                {/* Category header */}
                <button
                  onClick={() => toggleCategory(cat.key)}
                  className="w-full flex items-center justify-between px-5 py-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    {isExpanded ? (
                      <ChevronDown className="h-5 w-5 text-gray-400" />
                    ) : (
                      <ChevronRight className="h-5 w-5 text-gray-400" />
                    )}
                    <span className="font-semibold text-gray-800">{cat.label}</span>
                    <span className="text-xs text-gray-400 bg-gray-200 rounded-full px-2 py-0.5">
                      {catFaqs.length}
                    </span>
                  </div>
                  <Button
                    size="sm"
                    onClick={(e) => {
                      e.stopPropagation();
                      openCreateModal("support", cat.key);
                    }}
                    disabled={!apiService.getFlavourId()}
                    className="flex items-center gap-1"
                  >
                    <Plus className="h-3.5 w-3.5" />
                    <span>Add Question</span>
                  </Button>
                </button>

                {/* FAQ list inside category */}
                {isExpanded && (
                  <div className="p-4">
                    {catFaqs.length > 0 ? (
                      catFaqs.map(renderFaqRow)
                    ) : (
                      <p className="text-center text-gray-400 text-sm py-6">
                        No questions in this category yet
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* ─── Section 2: Tour FAQs ─── */}
      {activeTab === "tour" && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-gray-500">
              Tour-specific FAQs are always categorized as <strong>General</strong>.
            </p>
            <Button
              onClick={() => openCreateModal("tour")}
              disabled={!apiService.getFlavourId()}
              className="flex items-center gap-1"
            >
              <Plus className="h-4 w-4" />
              <span>Add FAQ</span>
            </Button>
          </div>

          {Object.keys(tourFaqsByTour).length > 0 ? (
            <div className="space-y-4">
              {Object.entries(tourFaqsByTour).map(([tourId, { tourName, faqs }]) => (
                <div key={tourId} className="border border-gray-200 rounded-lg overflow-hidden">
                  <div className="px-5 py-3 bg-gray-50 border-b border-gray-200">
                    <span className="font-semibold text-gray-800">{tourName}</span>
                    <span className="text-xs text-gray-400 bg-gray-200 rounded-full px-2 py-0.5 ml-2">
                      {faqs.length}
                    </span>
                  </div>
                  <div className="p-4">{faqs.map(renderFaqRow)}</div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12">
              <div className="bg-gray-50 rounded-lg p-8">
                <HelpCircle className="h-12 w-12 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-gray-900 mb-2">
                  No Tour FAQs Added Yet
                </h3>
                <p className="text-gray-500 text-sm">
                  Click "Add FAQ" to add questions for specific tours.
                </p>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ─── Create / Edit Modal ─── */}
      <Modal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        title={editingFaq ? "Edit FAQ" : "Add FAQ"}
        size="lg"
      >
        <div className="space-y-5">
          {/* Category (read-only for support, hidden for tour) */}
          {modalType === "support" && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
              <div className="bg-gray-100 border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-700 font-medium">
                {SUPPORT_CATEGORIES.find((c) => c.key === modalCategory)?.label || modalCategory}
              </div>
            </div>
          )}

          {/* Tour selection (only for tour type, only on create) */}
          {modalType === "tour" && !editingFaq && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Select Tour(s)*
              </label>
              <Select
                isMulti
                value={tourOptions.filter((o) => selectedTourIds.includes(o.value))}
                onChange={(selected: any) => {
                  setSelectedTourIds(selected ? selected.map((s: any) => s.value) : []);
                }}
                options={tourOptions}
                placeholder="Select tours..."
                className="react-select-container"
                classNamePrefix="react-select"
                styles={{
                  control: (base: any, state: any) => ({
                    ...base,
                    backgroundColor: "#ffffff",
                    borderColor: state.isFocused ? "#3b82f6" : "#cbd5e1",
                    boxShadow: state.isFocused ? "0 0 0 1px #3b82f6" : "none",
                    "&:hover": { borderColor: "#3b82f6" },
                  }),
                }}
              />
            </div>
          )}

          {modalType === "tour" && editingFaq && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Tour</label>
              <div className="bg-gray-100 border border-gray-200 rounded-lg px-4 py-2.5 text-sm text-gray-700">
                {tourOptions.find((o) => o.value === String(editingFaq.tourId))?.label || `Tour #${editingFaq.tourId}`}
              </div>
            </div>
          )}

          {/* Translation rows */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Questions & Answers
            </label>
            <div className="space-y-3">
              {questionTranslations.map((qt, index) => {
                const at = answerTranslations[index] || { language: qt.language, text: "" };
                return (
                  <div
                    key={index}
                    className="border border-gray-200 rounded-lg p-4 bg-white"
                  >
                    <div className="flex items-center gap-3 mb-3 pb-2 border-b border-gray-100">
                      <span className="text-xs font-bold text-gray-400 bg-gray-100 rounded-full w-6 h-6 flex items-center justify-center">
                        {index + 1}
                      </span>
                      <select
                        value={qt.language}
                        onChange={(e) => updateLanguageForRow(index, e.target.value)}
                        className="bg-white border border-gray-300 text-sm rounded-lg px-3 py-1.5 outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="">Select Language</option>
                        {languages.map((lang: any) => (
                          <option key={lang.code} value={lang.code}>
                            {lang.name}
                          </option>
                        ))}
                      </select>
                      {questionTranslations.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeTranslationRow(index)}
                          className="ml-auto p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                          title="Remove language"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>

                    <div className="mb-3">
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Question*
                      </label>
                      <input
                        type="text"
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                        value={qt.text}
                        onChange={(e) =>
                          setQuestionTranslations(
                            questionTranslations.map((item, i) =>
                              i === index ? { ...item, text: e.target.value } : item
                            )
                          )
                        }
                        placeholder="Enter question"
                      />
                    </div>

                    <div>
                      <label className="block text-xs font-medium text-gray-500 mb-1">
                        Answer*
                      </label>
                      <textarea
                        className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:border-blue-500 outline-none"
                        value={at.text}
                        onChange={(e) =>
                          setAnswerTranslations(
                            answerTranslations.map((item, i) =>
                              i === index ? { ...item, text: e.target.value } : item
                            )
                          )
                        }
                        placeholder="Enter answer"
                        rows={3}
                      />
                    </div>
                  </div>
                );
              })}
            </div>

            <div className="flex justify-center mt-3">
              <Button type="button" variant="outline" size="sm" onClick={addTranslationRow}>
                <Plus className="h-4 w-4 mr-1" />
                Add Language
              </Button>
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
            <Button variant="outline" onClick={handleCloseModal} disabled={isSubmitting}>
              Cancel
            </Button>
            <Button onClick={handleSubmit} disabled={isSubmitting} loading={isSubmitting}>
              {isSubmitting
                ? editingFaq ? "Updating..." : "Creating..."
                : editingFaq ? "Update FAQ" : "Create FAQ"}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default FaqComponent;
